// src/components/frontpage/NewsMiniGrid.jsx
// Responsive wrapped grid of the small horizontal news cards NewsBar used for its rotating
// strip — reused here (Locality + Groups front pages) as a scrollable wrap instead of an
// auto-rotating carousel. Same card visuals as NewsBar (image left + title right); Michael
// asked for these "mini" cards specifically, not NewsGrid's larger 250x250 squares.
//
// CSS grid with auto-fit/minmax, not a fixed-width flex-wrap: cards stretch to fill any slack
// in a row (auto-fit collapses unused tracks into the existing cards) AND a new column appears
// once the container is wide enough for another MIN_CARD_WIDTH-plus-gap slot — no JS width
// math, no fixed breakpoint, scales with whatever width the News column ends up with.
//
// Phase 2 tier (docs/briefs/2026-09-04-news-minicard-phase2-build.md + addendum-1): a
// MicroCard->MiniCard->Detail concertina, mirroring EventsColumn's REPLACE model exactly
// (addendum-1 item 1 — the first build's "supplement" version, which left both cards on screen
// duplicating the same image/title, was corrected to match Events). While a card is open its
// MicroCard is not rendered at all; the MiniCard is that article's sole representation, and it
// opens as a grid item spanning every column (`gridColumn: '1 / -1'`) since — unlike Events'
// single column — this is a multi-column grid, and a MiniCard expanding inside its own cell
// would balloon that cell and leave dead space beside it. `gridAutoFlow: 'dense'` lets the
// following MicroCards backfill the gap left by the removed one, so nothing is ever left dead —
// with the MicroCard actually gone (not just visually supplemented), dense packing has a real
// gap to fill and the full-width MiniCard sits correctly in flow. Degrades to exactly the Events
// accordion once the grid is one column wide (narrow column, mobile), since a full-width span
// and the only column are then the same thing.
//
// Single-open-per-grid and scroll-into-view reuse EventsColumn's own pair unchanged
// (useExpandInPlace / useScrollIntoViewOnOpen) rather than a second copy — D1's reasoning.

import React from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import InitialPlaceholder from '../core/InitialPlaceholder';
import { useExpandInPlace } from '../../hooks/useExpandInPlace';
import { useScrollIntoViewOnOpen } from '../../hooks/useScrollIntoViewOnOpen';
import NewsMiniCard from '../news/NewsMiniCard';

const MIN_CARD_WIDTH = 240;
const IMAGE_HEIGHT = 81;

const NewsMiniGrid = ({ articles = [], onArticleClick }) => {
  const { t } = useTranslation('ui');
  const { openId, isOpen, toggle: toggleCard } = useExpandInPlace();
  const { setRef } = useScrollIntoViewOnOpen(openId);

  if (articles.length === 0) {
    return (
      <div style={{
        padding: '24px', textAlign: 'center', color: '#6b7280',
        fontFamily: 'OpenSauceSans, sans-serif', fontSize: '13px'
      }}>
        {t('no_articles_found')}
      </div>
    );
  }

  return (
    <div style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'thin' }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fit, minmax(${MIN_CARD_WIDTH}px, 1fr))`,
        // dense, not the default sparse: removing the clicked MicroCard leaves a real gap in the
        // row it occupied, and dense packing lets the next MicroCards in DOM order backfill it
        // instead of leaving it dead — see file header.
        gridAutoFlow: 'dense',
        gap: '10px',
        padding: '2px'
      }}>
        {articles.map(article => {
          const open = isOpen(article.id);

          // MiniCard — REPLACES the MicroCard cell while open (addendum-1 item 1, matching
          // EventsColumn's own "MicroCard <-> MiniCard is a REPLACE, not a supplement"), as a
          // grid item spanning every column. `useScrollIntoViewOnOpen`'s ref keeps it anchored
          // where the MicroCard was — D5's "clicked card's top edge stays visible" refers to the
          // opened card itself staying anchored at the click point, not to the now-redundant
          // MicroCard persisting alongside it.
          if (open) {
            return (
              <div key={article.id} ref={setRef(article.id)} style={{ gridColumn: '1 / -1' }}>
                <NewsMiniCard
                  article={article}
                  onViewDetails={() => onArticleClick?.(article.id)}
                  onCollapse={() => toggleCard(article.id)}
                />
              </div>
            );
          }

          // MicroCard — unchanged visuals; click toggles the tier instead of navigating straight
          // to Detail (the split-click-semantics ruling — Detail is reached via the MiniCard's
          // "More" instead).
          return (
            <div
              key={article.id}
              onClick={() => toggleCard(article.id)}
              style={{
                minWidth: 0,
                display: 'flex',
                flexDirection: 'row',
                gap: '8px',
                cursor: 'pointer',
                padding: '6px',
                borderRadius: '6px',
                background: '#f9fafb',
                overflow: 'hidden',
                transition: 'background 0.15s'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f4f8'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#f9fafb'; }}
            >
              {article.cover_image_url ? (
                <img
                  src={article.cover_image_url}
                  alt=""
                  loading="lazy"
                  style={{
                    height: `${IMAGE_HEIGHT}px`,
                    aspectRatio: '4 / 3',
                    objectFit: 'cover',
                    borderRadius: '4px',
                    flexShrink: 0
                  }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <InitialPlaceholder
                  name={article.title}
                  fontSize="1.2rem"
                  style={{ height: `${IMAGE_HEIGHT}px`, aspectRatio: '4 / 3', borderRadius: '4px' }}
                />
              )}
              <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
                <div style={{
                  fontFamily: 'OpenSauceSans, sans-serif',
                  fontWeight: '600',
                  fontSize: '12px',
                  color: '#1f2937',
                  lineHeight: '1.35',
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical'
                }}>
                  {article.title}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default NewsMiniGrid;
